/**
 * WCO-2322 (6B) — Production-Output Scan Service
 * Frontend wrapper for scanning processed items (finished box / by-product case / FP-NI label) into a
 * per-destination production OUTPUT. Backend routes:
 *   services/inventory-service/src/routes/productionOutputScan.ts (mounted at /api/inventory/production-output)
 * All scan integrity rules (single-output uniqueness, session↔output binding, cross-type rejection) live in
 * the backend — this wrapper just relays barcode + scanner.
 */
import api from './api';

const BASE = '/inventory/production-output';

export interface OutputScan {
  id: string;
  barcode: string;
  scanned_by: string;
  scanned_at: string;
  item_number: string | null;
  actual_weight: number | null;
  source: 'finished' | 'byproduct' | 'fpni' | null;
  session_number?: string | null;   // WCO-2412: producing session (finished scans only) for group bucketing
  item_english_name?: string | null;
}

export interface OutputScanSummary {
  expected_cases: number;
  scanned_count: number;
  scanned_weight: number;
  by_item: Array<{ item_number: string; count: number; weight: number }>;
}

export interface OutputScansResponse {
  output: { output_number: string; production_date: string; destination_warehouse: string };
  scans: OutputScan[];
  summary: OutputScanSummary;
}

export const productionOutputScanService = {
  // Scan one barcode into the output. Throws on 400 (unresolvable / wrong session) / 404 / 409 (duplicate).
  async scanToOutput(outputNumber: string, barcode: string, scannedBy: string) {
    const res = await api.post(`${BASE}/${outputNumber}/scan`, { barcode, scanned_by: scannedBy });
    return res.data?.data ?? res.data;
  },

  async getOutputScans(outputNumber: string): Promise<OutputScansResponse> {
    const res = await api.get(`${BASE}/${outputNumber}/scan`);
    return res.data?.data ?? res.data;
  },

  async undoOutputScan(outputNumber: string, scanId: string): Promise<void> {
    await api.delete(`${BASE}/${outputNumber}/scan/${scanId}`);
  },

  // List active outputs across a date range (YYYY-MM-DD) — for stacking the day's outputs in the Transfers list.
  async getOutputsByRange(startDate: string, endDate: string): Promise<Array<{
    output_number: string; production_date: string; destination_warehouse: string; status: string;
    ship_status: 'draft' | 'pending' | 'complete';  // the output's own sent→received lifecycle
  }>> {
    const res = await api.get(BASE, { params: { start_date: startDate, end_date: endDate } });
    return res.data?.data ?? res.data ?? [];
  },
};
